import { useState, useEffect } from 'react'
import { setDocumentTitle } from 'shared/util'
import { useTenant } from 'features/tenant/context'

import {
  Container,
  ContainerProps,
  Box,
  Alert,
  Stack,
  Paper,
  List,
  useMediaQuery,
  useTheme
} from '@mui/material'

import { GetTenantBanner } from 'interface/tenant'

import { ErrorInfo } from 'components/controls/errorInfo'
import { Loading } from 'components/controls/loading'

import { ManufacturerBlock } from 'features/search/components/manufacturerBlock'
import { ManufacturerLinkItem } from 'features/search/components/manufacturerLinkItem'
import { SearchListHeader } from 'features/search/components/searchListHeader'
import { EntrySearchDialog } from 'features/search/dialog/entrySearchDialog'

import { useSearchListContainer } from './container'
import { useSideMenu } from 'components/sideMenu/container'
import { useTopBar } from 'components/topBar/container'

type SearchListPageProps = {
  topBarState: ReturnType<typeof useTopBar>
  sideMenuState: ReturnType<typeof useSideMenu>
  onSelectSearch: (searchId: number) => void
} & ContainerProps
export const SearchListPage = ({
	topBarState: { setBreadCrumbs },
	sideMenuState: { setActivePage },
  onSelectSearch,
  ...others
}: SearchListPageProps) => {
  const theme = useTheme()
  const isSmallMedia = useMediaQuery(theme.breakpoints.down('sm'))

  const tenant = useTenant()
  const [modal, setModal] = useState<'newSearch' | null>(null)
  const [tenantBanner, setTenantBanner] = useState<string | undefined>()

  const {
    searchWord, setSearchWord,
    loading, error,
    manufacturerList
  } = useSearchListContainer()

  useEffect(() => {
    GetTenantBanner(tenant.domain)
      .catch((reason) => console.log(reason))
      .then((response) =>
        setTenantBanner(
          response ? URL.createObjectURL(response.blob) : undefined
        )
      )
  }, [tenant.domain, tenant.tenantId])

  useEffect(
    () => {
      if (manufacturerList && manufacturerList.length > 0) {
        const hashSave = window.location.hash
        window.location.hash = ""
        window.location.hash = hashSave
      }
    },
    [manufacturerList]
  )

  useEffect(
    () => {
      setBreadCrumbs([{ label: '分析一覧' }])
      setDocumentTitle('分析一覧', tenant.tenantName)
      setActivePage('searchList')
    },
    [tenant, setBreadCrumbs, setActivePage]
  )
  if (loading) return <Loading />
  return (
    <Container
      {...others}
      maxWidth="xl"
      sx={{
        height: '100%',
        maxHeight: '100%',
        overflow: 'hidden',
        padding: isSmallMedia ? 0 : 2
      }}
    >
      <Paper
        square={isSmallMedia}
        sx={{
          height: '100%',
          overflowX: 'hidden',
          overflowY: isSmallMedia ? 'auto' : 'hidden',
          scrollBehavior: 'smooth'
        }}
      >
        {error && <ErrorInfo padding={1} error={error} />}
        {manufacturerList && (
          <Stack direction={isSmallMedia ? "column" : "row"} height={isSmallMedia ? undefined : "100%"}>
            <Box
              height='100%'
              flexShrink={0}
              boxSizing='border-box'
              sx={isSmallMedia ? undefined : {
                overflowX: 'hidden',
                overflowY: 'auto'
              }}
            >
              {tenantBanner && (
                <Box
                  padding={1}
                  sx={{
                    position: "sticky",
                    top: 0,
                    bgcolor: 'background.paper',
                    zIndex: 10
                  }}
                >
                  <img
                    src={tenantBanner}
                    alt={tenant.tenantName}
                    width="100%"
                    style={{
                      maxHeight: '5em',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              )}

              <List disablePadding dense component="nav">
                {manufacturerList.map((manufacturer) => (
                  <ManufacturerLinkItem
                    key={`manufacturer-link-${manufacturer.manufacturerId}`}
                    isSmallMedia={isSmallMedia}
                    {...manufacturer}
                  />
                ))}
              </List>
            </Box>

            <Stack flexGrow={1} direction="column" height="100%">
              <SearchListHeader
                searchWord={searchWord}
                setSearchWord={setSearchWord}
                onOpenNewSearchDialog={() => setModal('newSearch')}
                isSmallMedia={isSmallMedia}
                sx={isSmallMedia ? {
                  position: "sticky",
                  top: 0,
                  bgcolor: 'background.paper',
                  zIndex: 10
                } : undefined}
              />
              <Box overflow='hidden' flexGrow={1}>
                {manufacturerList.length === 0 ? (
                  <Box padding={1} height='100%' overflow='auto'>
                    <Alert severity="info">対象が見つかりません.</Alert>
                  </Box>
                ) : manufacturerList.length === 1 ? (
                  <Box padding={1} height='100%' overflow='auto'>
                    <ManufacturerBlock
                      manufacturer={manufacturerList[0]}
                      onSelectSearch={onSelectSearch}
                    />
                  </Box>
                ) : (
                  <Stack
                    direction="column"
                    flexGrow={1}
                    height='100%'
                    boxSizing='border-box'
                    sx={{
                      overflowX: 'hidden',
                      overflowY: 'auto',
                      scrollBehavior: 'smooth'
                    }}
                  >
                    {manufacturerList.map((manufacturer) => (
                      <ManufacturerBlock
                        key={`manufacturer-${manufacturer.manufacturerId}`}
                        id={`manufacturer-${manufacturer.manufacturerId}`}
                        manufacturer={manufacturer}
                        onSelectSearch={onSelectSearch}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          </Stack>
        )}
      </Paper>

      {modal === 'newSearch' ? (
        <EntrySearchDialog
          onSubmit={(search) => {
            setModal(null)
            onSelectSearch(search.searchId)
          }}
          onClose={() => setModal(null)}
        />
      ) : (
        <></>
      )}
    </Container>
  )
}
